import { Handle, Position, type Node, type NodeProps } from '@xyflow/react';
import { Box, Braces, Brackets, CircleDot, Component, Cuboid, Database, FlaskConical, Hammer, Hash, KeyRound, Link2, ListOrdered, Lock, Package, Sigma, Sparkles, SquareFunction, VenetianMask, Workflow, Wrench, Zap } from 'lucide-react';
import { memo } from 'react';
import { cn } from '../lib/cn';
import { useDiagramInteraction } from './context';
import { approximateMeasure, DIFF_LABEL, EFFECT_LABEL, type EntityNodeData, type Line, type MemberIcon, type RowData, type SectionData } from './graph';

export type EntityNodeType = Node<EntityNodeData, 'entity'>;

const DIFF_TITLE = { added: 'Added', removed: 'Removed', changed: 'Changed' } as const;

/** Code map members: one icon per kind, as code editors show them. */
const MEMBER_ICONS: Record<MemberIcon, { icon: typeof Box; label: string; className: string }> = {
  method: { icon: SquareFunction, label: 'Method', className: 'text-accent' },
  constructor: { icon: Hammer, label: 'Constructor', className: 'text-accent' },
  operator: { icon: Sigma, label: 'Operator', className: 'text-accent' },
  property: { icon: Wrench, label: 'Property', className: 'text-muted' },
  field: { icon: CircleDot, label: 'Field', className: 'text-faint' },
  event: { icon: Zap, label: 'Event', className: 'text-changed' },
  const: { icon: Hash, label: 'Constant', className: 'text-faint' },
  enumMember: { icon: Hash, label: 'Enum member', className: 'text-faint' },
  data: { icon: Database, label: 'Data (an EF Core entity)', className: 'text-changed' },
  fake: { icon: VenetianMask, label: 'Replaced by a fake in this test', className: 'text-muted' },
};

/** Code map types, by the keyword shown in the section's type column. */
const TYPE_ICONS: Record<string, typeof Box> = {
  class: Box,
  record: Cuboid,
  struct: Brackets,
  'record struct': Brackets,
  interface: Component,
  enum: ListOrdered,
  delegate: Workflow,
};

function SectionIcon({ section }: { section: SectionData }) {
  if (section.kind === 'owned') return <Package size={12} aria-hidden />;
  if (section.kind === 'complex') return <Braces size={12} aria-hidden />;
  const Icon = TYPE_ICONS[section.type] ?? Box;
  return <Icon size={12} aria-hidden />;
}

function RowIcon({ row }: { row: RowData }) {
  if (row.member) {
    const { icon: Icon, label, className } = MEMBER_ICONS[row.member];
    return <Icon size={12} className={className} aria-label={label} />;
  }
  if (row.pk) return <KeyRound size={12} className="text-changed" aria-label="Primary key" />;
  if (row.ak) return <KeyRound size={12} className="text-faint" aria-label="Alternate key" />;
  if (row.fk) return <Link2 size={12} className="text-accent" aria-label="Foreign key" />;
  return null;
}

function RowHandles({ id }: { id: string }) {
  return (
    <>
      <Handle id={`${id}:L`} type="source" position={Position.Left} className="row-handle" isConnectable={false} />
      <Handle id={`${id}:R`} type="source" position={Position.Right} className="row-handle" isConnectable={false} />
    </>
  );
}

interface LineProps {
  line: Line;
  nodeId: string;
  anchored: boolean;
  selected: boolean;
  selectRow: (nodeId: string, row: RowData) => void;
}

function LineView({ line, nodeId, anchored, selected, selectRow }: LineProps) {
  if (line.kind === 'section') {
    const s = line.section;
    return (
      <div className={cn('mv-section', s.diff && `diff-${s.diff}`)} style={{ paddingLeft: 10 + s.depth * 12 }} title={s.diff ? DIFF_TITLE[s.diff] : undefined}>
        <SectionIcon section={s} />
        <span className="mv-section-name">{s.name}</span>
        <span className="mv-type">
          {s.type}
          {s.collection ? '[]' : ''}
          {s.optional ? '?' : ''}
        </span>
        {s.json && <span className="mv-tag">json</span>}
      </div>
    );
  }
  const r = line.row;
  const title = r.diff ? [DIFF_TITLE[r.diff], r.note].filter(Boolean).join(': ') : undefined;
  return (
    <div
      className={cn('mv-row nodrag', selected && 'selected', r.diff && `diff-${r.diff}`)}
      style={{ paddingLeft: 8 + r.depth * 12 }}
      title={title}
      onClick={(e) => {
        e.stopPropagation();
        selectRow(nodeId, r);
      }}
    >
      <span className="mv-icon">
        <RowIcon row={r} />
      </span>
      <span className={cn('mv-name', r.optional && 'optional')}>{r.name}</span>
      {r.generated && <Sparkles size={11} className="mv-flag" aria-label="Generated by the database" />}
      {r.concurrency && <Lock size={11} className="mv-flag" aria-label="Concurrency token" />}
      {r.tests ? (
        <span className="mv-tests" title={`Reached by ${r.tests} ${r.tests === 1 ? 'test' : 'tests'}`} aria-label={`${r.tests} ${r.tests === 1 ? 'test' : 'tests'}`}>
          <FlaskConical size={10} aria-hidden />
          {r.tests}
        </span>
      ) : null}
      <span className="mv-type">{r.type}</span>
      {anchored && <RowHandles id={r.id} />}
    </div>
  );
}

/**
 * A line drawn from far away: a bar where the name is and one where the type is, one element
 * instead of seven or more. It keeps the row's height and handles, so lines stay attached.
 */
function LineBar({ line, width, anchored }: { line: Line; width: number; anchored: boolean }) {
  const isRow = line.kind === 'row';
  const depth = isRow ? line.row.depth : line.section.depth;
  const name = isRow ? line.row.name : line.section.name;
  const type = isRow ? line.row.type : line.section.type;
  const diff = isRow ? line.row.diff : line.section.diff;
  // Where the name starts (after the icon column) and the room left for both bars.
  const x = 28 + depth * 12;
  const room = Math.max(0, width - x - 12);
  const nameWidth = Math.min(approximateMeasure(name, isRow ? 'name' : 'section'), room * 0.62);
  const typeWidth = Math.min(approximateMeasure(type, 'type'), room - nameWidth - 12);
  const kind = isRow
    ? line.row.member
      ? line.row.member === 'method' || line.row.member === 'constructor' || line.row.member === 'operator'
        ? 'fk'
        : null
      : line.row.pk
        ? 'pk'
        : line.row.fk
          ? 'fk'
          : line.row.optional
            ? 'optional'
            : null
    : 'section';
  return (
    <div
      className={cn('mv-bar', kind && `mv-bar-${kind}`, diff && `diff-${diff}`)}
      style={{ '--x': `${x}px`, '--name': `${Math.round(nameWidth)}px`, '--type': `${Math.max(0, Math.round(typeWidth))}px` } as React.CSSProperties}
    >
      {anchored && line.kind === 'row' && <RowHandles id={line.row.id} />}
    </div>
  );
}

/**
 * A click handler makes the header a tap target of its own. Touch browsers move a tap onto the
 * nearest element with one, so a tap on a small card's title used to select a property row.
 * The click itself goes on to the card, which selects it.
 */
function keepTap(): void {}

interface CardProps {
  data: EntityNodeData;
  selected: boolean;
  dimmed: boolean;
  /** Drawn softer, still readable: cascade paths are on and the delete does not reach it. */
  soft: boolean;
  editable: boolean;
  zoomedOut: boolean;
  /** The selected row's id when it is on this card. */
  selectedRow: string | null;
  selectRow: (nodeId: string, row: RowData) => void;
}

// The card's content only draws again when what it shows changes, not each time the card moves
// (React Flow passes positions to the node, so a glide to a new layout would redraw every row).
const Card = memo(function Card({ data, selected, dimmed, soft, editable, zoomedOut, selectedRow, selectRow }: CardProps) {
  const anchors = new Set(data.anchors);
  return (
    <div
      className={cn(
        'mv-node',
        selected && 'selected',
        dimmed && 'dimmed',
        soft && 'soft',
        data.muted && 'mv-muted',
        zoomedOut && 'mv-far',
        data.diff && `diff-${data.diff}`,
        data.effects?.[0] && `effect-${data.effects[0]}`,
        data.deleteStart && 'delete-start',
      )}
      style={{ width: data.width }}
    >
      <div className="mv-node-header" title={data.namespace ? `${data.namespace}.${data.label}` : data.label} onClick={keepTap}>
        <span className="mv-node-title">{data.label}</span>
        {data.base && <span className="mv-node-base">: {data.base}</span>}
        {data.note && <span className="mv-node-note">{data.note}</span>}
        <span className="mv-node-badges">
          {data.badges.map((b) => (
            <span key={b} className="mv-tag">
              {b}
            </span>
          ))}
          {data.diff && <span className={cn('mv-tag', `diff-${data.diff}`)}>{DIFF_LABEL[data.diff]}</span>}
        </span>
        {(data.deleteStart || data.effects) && (
          <span className="mv-node-effects">
            {data.deleteStart && <span className="mv-tag effect-start">deleting one</span>}
            {data.effects?.map((e) => (
              <span key={e} className={cn('mv-tag', `effect-${e}`)}>
                {EFFECT_LABEL[e]}
              </span>
            ))}
          </span>
        )}
        <Handle id="__node:L" type="source" position={Position.Left} className="row-handle" isConnectable={false} />
        <Handle id="__node:R" type="source" position={Position.Right} className="row-handle" isConnectable={false} />
        {editable && data.diff !== 'removed' && (
          <Handle id="__connect" type="source" position={Position.Right} className="mv-connect" isConnectableEnd={false} title="Drag to the entity this one belongs to" />
        )}
      </div>
      {/* Covers the card so a connection can be dropped anywhere on it. It must exist before a drag
          starts (React Flow collects handles then) and only takes the pointer while connecting. */}
      {editable && data.diff !== 'removed' && <Handle id="__drop" type="target" position={Position.Top} className="mv-drop" isConnectableStart={false} />}
      {data.lines.length > 0 && (
        <div className="mv-node-body">
          {data.lines.map((line) => {
            const removed = (line.kind === 'row' ? line.row.diff : line.section.diff) === 'removed';
            const id = line.kind === 'row' ? line.row.id : `§${line.section.id}`;
            const anchored = line.kind === 'row' && anchors.has(line.row.id);
            const key = removed ? `-${id}` : id;
            return zoomedOut ? (
              <LineBar key={key} line={line} width={data.width} anchored={anchored} />
            ) : (
              <LineView key={key} line={line} nodeId={data.id} anchored={anchored} selected={line.kind === 'row' && selectedRow === line.row.id} selectRow={selectRow} />
            );
          })}
        </div>
      )}
    </div>
  );
});

export const EntityNode = memo(function EntityNode({ data, selected }: NodeProps<EntityNodeType>) {
  const { matches, emphasis, editable, selectedRow, selectRow, zoomedOut } = useDiagramInteraction();
  const prefix = `${data.id}::`;
  const dimmed = matches !== null && !matches.has(data.id);
  return (
    <Card
      data={data}
      selected={!!selected}
      dimmed={dimmed}
      soft={!dimmed && emphasis !== null && !emphasis.has(data.id)}
      editable={editable}
      zoomedOut={zoomedOut}
      selectedRow={selectedRow?.startsWith(prefix) ? selectedRow.slice(prefix.length) : null}
      selectRow={selectRow}
    />
  );
});
